import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiMapPin, FiShoppingCart } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { AdminContext } from '../../Context/AdminContext';
import { apiRequest } from '../../Services/api';
import { CartDrawer, PickupSteps, ProductCard, formatCurrency } from './StorefrontComponents';
import './ProductsPage.css';

const PAGE_TITLE = "Shop Boiled Peanuts | Aunt Jack's Peanut Shack";
const PAGE_DESCRIPTION = 'Order small-batch Original, Bayou Cajun, Dill Pickle, and seasonal boiled peanuts from Aunt Jack’s Peanut Shack for local pickup.';
const CANONICAL_URL = 'https://www.auntjackspeanutshack.com/shop/';

function setMeta(selector, attribute, value) {
  const element = document.head.querySelector(selector);
  if (element) element.setAttribute(attribute, value);
}

export default function ProductsPage() {
  const { user, items, addItems, updateItem, removeItems, cartBusy } = useContext(AdminContext);
  const [products, setProducts] = useState([]);
  const [policy, setPolicy] = useState({ pickupLeadDays: 21, minPickupDate: '' });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [cartOpen, setCartOpen] = useState(false);
  const [addingProductId, setAddingProductId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const cartCount = useMemo(() => items.reduce((total, item) => total + Number(item.selectedQuantity || 0), 0), [items]);
  const cartSubtotal = useMemo(() => items.reduce((total, item) => total + Number(item.lineTotal || 0), 0), [items]);
  const closeCart = useCallback(() => setCartOpen(false), []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await apiRequest('/products/index.php');
      setProducts(Array.isArray(data.products) ? data.products : []);
      if (data.orderingPolicy) setPolicy(data.orderingPolicy);
    } catch (error) {
      setLoadError(error.message || 'The shop could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  useEffect(() => {
    if (location.state?.openCart) {
      setCartOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const priorTitle = document.title;
    const description = document.head.querySelector('meta[name="description"]');
    const robots = document.head.querySelector('meta[name="robots"]');
    const canonical = document.head.querySelector('link[rel="canonical"]');
    const priorDescription = description?.getAttribute('content');
    const priorRobots = robots?.getAttribute('content');
    const priorCanonical = canonical?.getAttribute('href');
    document.title = PAGE_TITLE;
    setMeta('meta[name="description"]', 'content', PAGE_DESCRIPTION);
    setMeta('meta[property="og:title"]', 'content', PAGE_TITLE);
    setMeta('meta[property="og:description"]', 'content', PAGE_DESCRIPTION);
    setMeta('meta[property="og:url"]', 'content', CANONICAL_URL);
    setMeta('meta[name="robots"]', 'content', 'index,follow');
    canonical?.setAttribute('href', CANONICAL_URL);
    return () => {
      document.title = priorTitle;
      if (priorDescription) description?.setAttribute('content', priorDescription);
      if (priorRobots) robots?.setAttribute('content', priorRobots);
      if (priorCanonical) canonical?.setAttribute('href', priorCanonical);
    };
  }, []);

  useEffect(() => {
    if (!products.length) return undefined;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'shop-structured-data';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org', '@type': 'ItemList', name: "Aunt Jack's boiled peanuts",
      itemListElement: products.map((product, index) => ({
        '@type': 'ListItem', position: index + 1,
        item: {
          '@type': 'Product', name: product.productName, description: product.description,
          image: new URL(product.imageUrl, CANONICAL_URL).href,
          offers: product.sizes?.length ? {
            '@type': 'AggregateOffer', priceCurrency: 'USD',
            lowPrice: Math.min(...product.sizes.map((size) => Number(size.price))).toFixed(2),
            highPrice: Math.max(...product.sizes.map((size) => Number(size.price))).toFixed(2),
            offerCount: product.sizes.length, availability: 'https://schema.org/InStock',
          } : undefined,
        },
      })),
    });
    document.getElementById(script.id)?.remove();
    document.head.appendChild(script);
    return () => script.remove();
  }, [products]);

  const handleAdd = async (product) => {
    if (!user) {
      navigate('/auth', { state: { returnTo: '/shop', pendingCartItem: {
        id: Number(product.id), selectedSizeId: Number(product.selectedSizeId), selectedQuantity: Number(product.selectedQuantity),
      } } });
      return;
    }
    setAddingProductId(product.id);
    try {
      await addItems(product);
      setCartOpen(true);
      toast.success(`${product.productName} added to your cart.`);
    } catch (error) {
      toast.error(error.message || 'Unable to add this item.');
    } finally {
      setAddingProductId(null);
    }
  };

  const handleCartUpdate = async (id, quantity) => {
    try { await updateItem(id, quantity); } catch (error) { toast.error(error.message || 'Unable to update the cart.'); }
  };

  const handleCartRemove = async (id) => {
    try { await removeItems(id); } catch (error) { toast.error(error.message || 'Unable to remove that item.'); }
  };

  return (
    <main className='storefront'>
      <section className='store-hero'>
        <div className='store-hero-copy'>
          <h1>Boiled peanuts,<br />made for gathering.</h1>
          <p>Choose your flavor and size, then select a pickup date at checkout.</p>
        </div>
        <img src='/images/products/shop-hero.jpg' alt='A steaming pot filled with freshly boiled peanuts' width='1536' height='1024' />
      </section>

      <section className='store-catalog' id='shop-the-boil' aria-labelledby='shop-heading'>
        <div className='store-section-heading'>
          <div><h2 id='shop-heading'>Shop the boil</h2><p><FiMapPin aria-hidden='true' /> Local pickup <span>·</span> Please allow at least {policy.pickupLeadDays} days</p></div>
          {user ? (
            <button className='store-cart-button' type='button' onClick={() => setCartOpen(true)} aria-label={`Open cart with ${cartCount} items`}>
              <FiShoppingCart aria-hidden='true' /><span>Cart</span><strong>{cartCount}</strong>
            </button>
          ) : <Link className='store-signin-link' to='/auth' state={{ returnTo: '/shop' }}>Sign in to order</Link>}
        </div>

        {loading ? (
          <div className='store-product-grid' aria-label='Loading products'>{[0, 1, 2].map((item) => <div className='store-product-skeleton' key={item} />)}</div>
        ) : loadError ? (
          <div className='store-status-panel' role='alert'><h3>We couldn’t open the shop.</h3><p>{loadError}</p><button type='button' onClick={loadProducts}>Try again</button></div>
        ) : products.length ? (
          <div className='store-product-grid'>{products.map((product) => <ProductCard product={product} onAdd={handleAdd} busy={addingProductId === product.id} key={product.id} />)}</div>
        ) : (
          <div className='store-status-panel'><h3>The next batch is simmering.</h3><p>No products are available right now. Please check back soon.</p></div>
        )}
      </section>

      <PickupSteps />

      {user && items.length ? (
        <button className='store-mobile-cart-bar' type='button' onClick={() => setCartOpen(true)}>
          <span><FiShoppingCart aria-hidden='true' /><strong>{cartCount}</strong> {cartCount === 1 ? 'item' : 'items'}</span><span>{formatCurrency(cartSubtotal)}</span>
        </button>
      ) : null}
      <CartDrawer open={cartOpen} onClose={closeCart} items={items} onUpdate={handleCartUpdate} onRemove={handleCartRemove} busy={cartBusy} policy={policy} />
    </main>
  );
}
